import { AgentPlatformSurface, useAgentProfiles } from '@commenda/api';
import { AgentProfileHelper, cn } from '@commenda/utils';
import { useMemo } from 'react';

import { ComposerPickerMenuHelper } from './composer-picker-menu-helper';
import { ComposerProfileMenuHelper } from './composer-profile-menu-helper';
import { ComposerPickerMenu } from './ComposerPickerMenu';
import type { IComposerProfilePickerConfig } from './types';

interface ComposerProfileMenuProps {
  profilePicker: IComposerProfilePickerConfig;
}

/** Picks the agent a new chat runs as (Figma A-M, node 836:107925). "General" carries no group so
 * it renders above the "Agents" section. */
export const ComposerProfileMenu = ({
  profilePicker,
}: ComposerProfileMenuProps) => {
  const { profiles, isLoading, error } = useAgentProfiles({
    surface: AgentPlatformSurface.CONVERSATION,
  });

  const options = useMemo(
    () => [
      {
        value: ComposerProfileMenuHelper.GENERAL_VALUE,
        label: ComposerProfileMenuHelper.GENERAL_LABEL,
        description: ComposerProfileMenuHelper.GENERAL_DESCRIPTION,
      },
      ...ComposerProfileMenuHelper.selectableProfiles(profiles ?? []).map(
        (profile) => ({
          value: profile.profileKey,
          label: AgentProfileHelper.getDisplayName(profile),
          description: profile.description,
          group: ComposerProfileMenuHelper.AGENTS_GROUP_LABEL,
        })
      ),
    ],
    [profiles]
  );

  const selectedValue =
    profilePicker.profileKey ?? ComposerProfileMenuHelper.GENERAL_VALUE;

  return (
    <ComposerPickerMenu
      options={options}
      value={selectedValue}
      onValueChange={(value) =>
        profilePicker.onProfileChange(
          ComposerProfileMenuHelper.toProfileKey(value)
        )
      }
      emptyText={ComposerProfileMenuHelper.getEmptyText(isLoading, error)}
      widthClassName="w-[340px]"
      trigger={
        <button
          type="button"
          aria-label="Choose an agent"
          className={cn(
            ComposerProfileMenuHelper.TRIGGER_CLASS_NAMES,
            'cursor-pointer hover:bg-bg-secondary'
          )}
        >
          <span className="truncate">
            {ComposerPickerMenuHelper.getSelectedLabel(
              options,
              selectedValue,
              ComposerProfileMenuHelper.GENERAL_LABEL
            )}
          </span>
        </button>
      }
    />
  );
};
